import classNames from 'classnames';
import * as _ from 'lodash-es';
import type { AttributeMap, Op } from 'quill/core';
import { Delta } from 'quill/core';
import { getSourceUrlWithAssetToken } from '../AssetTokenContext';
import { LINK_TYPES, LINK_TYPE_ATTRIBUTE } from '../constants';
import type { UploadedCarouselItem } from '../image/types';
import type { Mention } from '../types';
import getEditorMentionUrl from './getEditorMentionUrl';

type TagName = keyof HTMLElementTagNameMap;

interface Node {
  merge(next: Node): [Node] | [Node, Node];
  getHTML(): string;
}

class Text implements Node {
  constructor(readonly text: string) {}

  merge(next: Node): [Node] | [Node, Node] {
    if (!(next instanceof Text)) return [this, next];
    return [new Text(this.text + next.text)];
  }

  getHTML() {
    return _.escape(this.text).replaceAll(' ', '&nbsp;');
  }
}

class RawHTML implements Node {
  constructor(private html: string) {}

  merge(next: Node): [Node] | [Node, Node] {
    if (!(next instanceof RawHTML)) return [this, next];
    return [new RawHTML(this.html + next.html)];
  }

  getHTML() {
    return this.html;
  }
}

class Element implements Node {
  readonly children: Node[] | null;

  constructor(
    readonly tagName: TagName,
    children: Node[] | Node | null = [],
    readonly attributes: Record<string, string> = {},
  ) {
    if (!children) {
      this.children = null;
    } else {
      this.children = Array.isArray(children) ? children : [children];
    }
  }

  merge(next: Node): [Node] | [Node, Node] {
    if (
      next instanceof Element &&
      this.tagName === next.tagName &&
      _.isEqual(this.attributes, next.attributes) &&
      this.children &&
      next.children
    ) {
      return [
        new Element(
          this.tagName,
          [...this.children, ...next.children],
          this.attributes,
        ),
      ];
    }
    return [this, next];
  }

  getHTML(): string {
    const attrs = Object.entries(this.attributes)
      .map(([key, value]) => `${key}="${_.escape(value)}"`)
      .join(' ');
    const startTag = `<${this.tagName}${attrs.length ? ` ${attrs}` : ''}`;
    if (!this.children) {
      return `${startTag} />`;
    }
    const html = this.children.map((child) => child.getHTML()).join('');
    return `${startTag}>${html}</${this.tagName}>`;
  }
}

const optionalString = (value: unknown) =>
  typeof value === 'string' ? value : undefined;

const applyInlineAttribute = (
  node: Node,
  updater: (attributes: Record<string, string>) => Record<string, string>,
) => {
  if (node instanceof Element) {
    return new Element(node.tagName, node.children, updater(node.attributes));
  }
  return new Element('span', node, updater({}));
};

const inlineFormats: {
  name: string;
  map: (child: Node, value: unknown) => Element;
}[] = [
  {
    name: 'link',
    map: (child, value) =>
      new Element('a', child, {
        href: String(value),
        rel: 'nofollow noopener noreferrer ugc',
        target: '_blank',
      }),
  },
  { name: 'strike', map: (child) => new Element('s', child) },
  { name: 'italic', map: (child) => new Element('em', child) },
  { name: 'bold', map: (child) => new Element('strong', child) },
  { name: 'code', map: (child) => new Element('code', child) },
  {
    name: 'highlight',
    map: (child, value) =>
      applyInlineAttribute(child, (attributes) => ({
        ...attributes,
        class: classNames(attributes.class, `highlight-${String(value)}`),
      })),
  },
  {
    name: 'comment',
    map: (child, value) =>
      applyInlineAttribute(child, (attributes) => ({
        ...attributes,
        class: classNames(attributes.class, `comment-${String(value)}`),
      })),
  },
  {
    name: 'reaction',
    map: (child, value) =>
      applyInlineAttribute(child, (attributes) => ({
        ...attributes,
        class: classNames(attributes.class, `reaction-${String(value)}`),
      })),
  },
  {
    name: 'annotation',
    map: (child, value) =>
      applyInlineAttribute(child, (attributes) => ({
        ...attributes,
        class: classNames(attributes.class, `annotation-${String(value)}`),
      })),
  },
];

const blockToNodes = (block: Delta, assetToken?: string | null) => {
  const nodes: Node[] = [];
  block.forEach((op) => {
    const attributes = op.attributes || {};
    if (typeof op.insert === 'string') {
      const node = inlineFormats
        .filter(({ name }) => !!attributes[name])
        .reduce<Node>((prev, format) => {
          const value = attributes[format.name];
          return value ? format.map(prev, value) : prev;
        }, new Text(op.insert));
      const lastNode = nodes.pop();
      if (lastNode) {
        nodes.push(...lastNode.merge(node));
      } else {
        nodes.push(node);
      }
    } else {
      const type = getOpEmbedType(op);
      const handler = type && embedHandlers[type.key];
      if (handler) {
        const embedNode = handler.toNode(
          type.value,
          op.attributes ?? {},
          assetToken,
        );
        if (embedNode) {
          nodes.push(embedNode);
        }
      }
    }
  });
  return nodes;
};

type ListType = 'ordered' | 'bullet' | 'checked' | 'unchecked';

interface ListItem {
  delta: Delta;
  indent: number;
  type: ListType;
}

const getListType = (type: ListType): [string, string] => {
  const tag = type === 'ordered' ? 'ol' : 'ul';
  switch (type) {
    case 'checked':
      return [tag, ' data-list="checked"'];
    case 'unchecked':
      return [tag, ' data-list="unchecked"'];
    default:
      return [tag, ''];
  }
};

// Inspired by Quill's convertListHTML()
const listItemsToHTML = (
  items: ListItem[],
  lastIndent: number,
  types: ListType[],
  assetToken?: string | null,
): string => {
  if (items.length === 0) {
    const lastType = types.pop();
    if (!lastType) return '';
    const [endTag] = getListType(lastType);
    if (lastIndent <= 0) {
      return `</li></${endTag}>`;
    }
    return `</li></${endTag}>${listItemsToHTML([], lastIndent - 1, types, assetToken)}`;
  }
  const [{ delta, indent, type }, ...rest] = items as [ListItem, ...ListItem[]];
  const [tag, attribute] = getListType(type);
  if (indent > lastIndent) {
    types.push(type);
    if (indent === lastIndent + 1) {
      return `<${tag}><li${attribute}>${deltaToHTML(delta, {
        assetToken,
        preferOmittingParagraphTag: true,
      })}${listItemsToHTML(rest, indent, types, assetToken)}`;
    }
    return `<${tag}><li>${listItemsToHTML(items, lastIndent + 1, types, assetToken)}`;
  }
  const previousType = types[types.length - 1];
  if (indent === lastIndent && type === previousType) {
    return `</li><li${attribute}>${deltaToHTML(delta, {
      assetToken,
      preferOmittingParagraphTag: true,
    })}${listItemsToHTML(rest, indent, types, assetToken)}`;
  }
  const lastType = types.pop();
  if (!lastType) return '';
  const [endTag] = getListType(lastType);
  return `</li></${endTag}>${listItemsToHTML(items, lastIndent - 1, types, assetToken)}`;
};

interface Block {
  delta: Delta;
  attributes: AttributeMap;
}

interface Container {
  container: string;
  blocks: Block[];
}

const containerTypes = ['blockquote', 'list'];

const groupIntoContainers = (blocks: Block[]): (Block | Container)[] => {
  const grouped: (Block | Container)[] = [];
  blocks.forEach((block) => {
    const containerType = containerTypes.find((type) => block.attributes[type]);
    if (containerType) {
      const last = _.last(grouped);
      if (last && 'container' in last && last.container === containerType) {
        last.blocks.push(block);
      } else {
        grouped.push({ container: containerType, blocks: [block] });
      }
    } else {
      grouped.push(block);
    }
  });
  return grouped;
};

const embedHandlers: Record<
  string,
  {
    isBlock: boolean;
    toNode: (
      value: unknown,
      attributes: AttributeMap,
      assetToken?: string | null,
    ) => Node | null;
  }
> = {
  mention: {
    isBlock: false,
    toNode: (value, attributes) => {
      const mention = value as Mention;
      const content = optionalString(attributes.content);
      const url = getEditorMentionUrl({ ...mention, content });
      return new Element(
        'a',
        new Text(optionalString(attributes['mention-text']) || content || url),
        {
          [LINK_TYPE_ATTRIBUTE]: LINK_TYPES.mention,
          href: url,
        },
      );
    },
  },
  image: {
    isBlock: true,
    toNode: (value, attributes, assetToken) => {
      let image: Pick<UploadedCarouselItem, 'caption' | 'source'> | undefined;
      if (typeof value === 'string') {
        image = {
          source: value,
          caption: attributes.caption as string | undefined,
        };
      } else if (Array.isArray(value)) {
        image = value[0] as UploadedCarouselItem | undefined;
      }
      if (!image) return null;
      const caption = optionalString(image.caption);
      return new Element(
        'figure',
        _.compact([
          new Element('img', null, {
            src: getSourceUrlWithAssetToken(image.source, assetToken),
            alt: '',
          }),
          caption ? new Element('figcaption', new Text(caption)) : null,
        ]),
      );
    },
  },
};

const getOpEmbedType = (op: Op) => {
  if (typeof op.insert === 'object') {
    const key = Object.keys(op.insert)[0];
    if (key) {
      const value = op.insert[key];
      if (value) {
        return { key, value };
      }
    }
  }
  return null;
};

const appendNewlineCharToBlockEmbed = (delta: Delta) => {
  const newDelta = new Delta();
  delta.ops.forEach((op) => {
    newDelta.push(op);

    const type = getOpEmbedType(op);
    if (type && embedHandlers[type.key]?.isBlock === true) {
      newDelta.push({ insert: '\n' });
    }
  });

  return newDelta;
};

interface DeltaToNodesOptions {
  assetToken?: string | null;
  preferOmittingParagraphTag?: boolean;
}

const deltaToNodes = (delta: Delta, options: DeltaToNodesOptions = {}) => {
  const nodes: Node[] = [];
  const blocks: Block[] = [];
  appendNewlineCharToBlockEmbed(delta).eachLine((lineDelta, attributes) => {
    blocks.push({ delta: lineDelta, attributes });
  });

  let hasParagraphTags = false;

  const grouped = groupIntoContainers(blocks);
  grouped.forEach((group, index) => {
    if ('container' in group) {
      if (group.container === 'blockquote') {
        nodes.push(
          new Element(
            'blockquote',
            group.blocks.map(
              (block) =>
                new Element(
                  'div',
                  new RawHTML(
                    deltaToHTML(block.delta, {
                      assetToken: options.assetToken,
                      preferOmittingParagraphTag: true,
                    }),
                  ),
                  { class: 'ql-blockquote-block' },
                ),
            ),
          ),
        );
      } else if (group.container === 'list') {
        const listItems: ListItem[] = [];
        group.blocks.forEach((block) => {
          const { attributes } = block;
          listItems.push({
            delta: block.delta,
            indent:
              typeof attributes.indent === 'number' ? attributes.indent : 0,
            type: attributes.list as ListType,
          });
        });
        nodes.push(
          new RawHTML(listItemsToHTML(listItems, -1, [], options.assetToken)),
        );
      }
      return;
    }

    const blockNodes = blockToNodes(group.delta, options.assetToken);
    if (typeof group.attributes.header === 'number') {
      nodes.push(
        new Element(
          `h${Math.min(group.attributes.header, 6)}` as TagName,
          blockNodes,
        ),
      );
    } else if (
      options.preferOmittingParagraphTag &&
      !hasParagraphTags &&
      index === grouped.length - 1
    ) {
      nodes.push(...blockNodes);
    } else {
      hasParagraphTags = true;
      nodes.push(new Element('p', blockNodes));
    }
  });

  return nodes;
};

const deltaToHTML = (
  content: Delta | Delta['ops'],
  options: DeltaToNodesOptions = {},
) => {
  const nodes = deltaToNodes(new Delta(content), options);
  return nodes.map((node) => node.getHTML()).join('');
};

export default deltaToHTML;
